import React from 'react';
import { motion } from 'framer-motion';
import { ArrowRight, Award, TrendingUp, Users, Target } from 'lucide-react';

const CreativeHero = () => {
  const scrollToForm = () => {
    const formSection = document.querySelector('#contact-form');
    if (formSection) {
      formSection.scrollIntoView({ behavior: 'smooth' });
    }
  };

  const stats = [
    { icon: Award, value: '250+', label: 'Projects', sublabel: 'Delivered' },
    { icon: Users, value: '50+', label: 'Happy', sublabel: 'Clients' },
    { icon: TrendingUp, value: '15+', label: 'Years', sublabel: 'Experience' },
    { icon: Target, value: '98%', label: 'Client', sublabel: 'Satisfaction' }
  ];

  const metrics = [
    { label: 'Innovation', value: 98, color: '#d987ff' },
    { label: 'Quality', value: 99, color: '#88a2ff' },
    { label: 'Speed', value: 95, color: '#ffd1e7' },
    { label: 'Support', value: 100, color: '#b7fbff' }
  ];

  return (
    <section className="relative min-h-[90vh] overflow-hidden bg-gradient-to-br from-[#0a0015] via-[#1a0a2e] to-[#0f0a1a]">
      {/* Animated background grid */}
      <div className="absolute inset-0 opacity-10">
        <div className="absolute inset-0" style={{
          backgroundImage: 'linear-gradient(rgba(255,255,255,0.05) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,0.05) 1px, transparent 1px)',
          backgroundSize: '80px 80px'
        }} />
      </div>

      {/* Animated particles */}
      <div className="absolute inset-0 overflow-hidden">
        {[...Array(15)].map((_, i) => (
          <motion.div
            key={i}
            className="absolute w-2 h-2 bg-light-pink rounded-full"
            animate={{
              x: [Math.random() * 100 + '%', Math.random() * 100 + '%'],
              y: [Math.random() * 100 + '%', Math.random() * 100 + '%'],
              opacity: [0, 0.5, 0],
              scale: [0, 1.5, 0]
            }}
            transition={{
              duration: Math.random() * 15 + 10,
              repeat: Infinity,
              ease: "linear"
            }}
            style={{
              left: `${Math.random() * 100}%`,
              top: `${Math.random() * 100}%`
            }}
          />
        ))}
      </div>

      {/* Content */}
      <div className="relative z-10 min-h-[90vh] flex items-center">
        <div className="max-w-8xl mx-auto px-8 lg:px-16 xl:px-24 w-full py-20">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            {/* Left Content */}
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8 }}
              className="text-white"
            >
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: 0.2 }}
                className="inline-flex items-center gap-2 mb-6 px-4 py-2 rounded-full bg-white/10 backdrop-blur-sm border border-white/20"
              >
                <Award size={18} className="text-light-pink" />
                <span className="text-xs font-mono uppercase tracking-wider">Innovation & Excellence</span>
              </motion.div>

              <motion.h1
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: 0.3 }}
                className="text-5xl md:text-6xl lg:text-7xl font-bold mb-6 leading-tight tracking-tight"
                style={{ fontWeight: 700 }}
              >
                Transform Your
                <br />
                <span className="bg-gradient-to-r from-light-pink via-mid-purple to-light-blue bg-clip-text text-transparent">
                  Digital Presence
                </span>
              </motion.h1>

              <motion.p
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: 0.4 }}
                className="text-xl md:text-2xl text-white/80 mb-8 leading-relaxed max-w-2xl"
              >
                Full-service digital agency crafting innovative solutions that drive real business results and extraordinary experiences.
              </motion.p>

              {/* Stats Row */}
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: 0.5 }}
                className="grid grid-cols-2 md:grid-cols-4 gap-6 mb-10"
              >
                {stats.map((stat, idx) => (
                  <motion.div
                    key={idx}
                    initial={{ opacity: 0, scale: 0.8 }}
                    animate={{ opacity: 1, scale: 1 }}
                    transition={{ duration: 0.5, delay: 0.6 + idx * 0.1 }}
                    className="group"
                  >
                    <div className="flex items-start gap-3">
                      <div className="w-10 h-10 rounded-xl bg-white/10 flex items-center justify-center group-hover:bg-light-pink transition-colors">
                        <stat.icon size={20} className="text-white" />
                      </div>
                      <div>
                        <div className="text-2xl font-bold">{stat.value}</div>
                        <div className="text-xs text-white/60 leading-tight">
                          {stat.label}<br />{stat.sublabel}
                        </div>
                      </div>
                    </div>
                  </motion.div>
                ))}
              </motion.div>

              <motion.button
                onClick={scrollToForm}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: 0.7 }}
                whileHover={{ scale: 1.05, x: 5 }}
                whileTap={{ scale: 0.95 }}
                className="group px-10 py-5 rounded-full bg-white text-black font-mono text-sm uppercase tracking-wider font-bold hover:bg-light-pink transition-all inline-flex items-center gap-3 shadow-2xl"
              >
                Start Your Project
                <ArrowRight size={20} className="group-hover:translate-x-2 transition-transform" />
              </motion.button>
            </motion.div>

            {/* Right Content - Metrics Dashboard */}
            <motion.div
              initial={{ opacity: 0, x: 30 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8, delay: 0.3 }}
              className="relative"
            >
              <div className="relative bg-white/5 backdrop-blur-xl rounded-3xl p-8 border border-white/10 shadow-2xl">
                {/* Header */}
                <div className="mb-8">
                  <div className="flex items-center justify-between mb-2">
                    <h3 className="text-3xl font-bold text-white" style={{ fontWeight: 700 }}>Our Excellence</h3>
                    <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-light-pink to-mid-purple flex items-center justify-center">
                      <Award size={24} className="text-white" />
                    </div>
                  </div>
                  <p className="text-white/60 text-sm">Performance metrics that speak for themselves</p>
                </div>

                {/* Metrics Grid */}
                <div className="grid grid-cols-2 gap-4">
                  {metrics.map((metric, idx) => (
                    <motion.div
                      key={idx}
                      initial={{ opacity: 0, y: 20 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ duration: 0.5, delay: 0.8 + idx * 0.1 }}
                      className="bg-white/5 backdrop-blur-sm rounded-2xl p-5 border border-white/10 hover:border-white/30 transition-all group"
                    >
                      <div className="flex items-baseline justify-between mb-3">
                        <span className="text-4xl font-bold text-white" style={{ fontWeight: 700 }}>{metric.value}%</span>
                        <motion.div
                          initial={{ scale: 0 }}
                          animate={{ scale: 1 }}
                          transition={{ duration: 0.5, delay: 1 + idx * 0.1 }}
                          className="w-2 h-2 rounded-full"
                          style={{ backgroundColor: metric.color }}
                        />
                      </div>
                      <div className="text-sm font-semibold text-white/70 mb-3">{metric.label}</div>
                      <div className="w-full h-2 rounded-full bg-white/10 overflow-hidden">
                        <motion.div
                          initial={{ width: 0 }}
                          animate={{ width: `${metric.value}%` }}
                          transition={{ duration: 1.5, delay: 1.2 + idx * 0.15, ease: "easeOut" }}
                          className="h-full rounded-full"
                          style={{ 
                            background: `linear-gradient(90deg, ${metric.color}60, ${metric.color})`
                          }}
                        />
                      </div>
                    </motion.div>
                  ))}
                </div>

                {/* Bottom Info */}
                <motion.div
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  transition={{ duration: 0.6, delay: 1.5 }}
                  className="mt-6 pt-6 border-t border-white/10 flex items-center justify-between text-sm"
                >
                  <span className="text-white/60">Trusted by 50+ companies worldwide</span>
                  <div className="flex items-center gap-2">
                    <div className="flex -space-x-2">
                      {[
                        { name: 'TechFlow', initial: 'TF', color: '#3b82f6' },
                        { name: 'DataVibe', initial: 'DV', color: '#10b981' },
                        { name: 'CloudSync', initial: 'CS', color: '#f59e0b' }
                      ].map((company, i) => (
                        <div
                          key={i}
                          className="w-8 h-8 rounded-full flex items-center justify-center border-2 border-black"
                          style={{ backgroundColor: company.color }}
                          title={company.name}
                        >
                          <span className="text-white text-xs font-bold">
                            {company.initial}
                          </span>
                        </div>
                      ))}
                    </div>
                    <span className="text-white/60 text-xs">+47 more</span>
                  </div>
                </motion.div>
              </div>
            </motion.div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default CreativeHero;
